import React, { useState, useCallback } from 'react';
import { Building2, GraduationCap } from 'lucide-react';
import domainMap from '../data/domainMap.json';

/* ------------------------------------------------------------------ */
/*  Company & University logo components.                               */
/*  Domains are resolved via domainMap.json (built by the               */
/*  scripts/build-domain-map.js one-time script).                       */
/*  Favicons are fetched through /api/favicon which proxies Google and  */
/*  returns 404 for the default globe placeholder — onError triggers    */
/*  the fallback icon automatically. No CORS issues, no size filters.   */
/* ------------------------------------------------------------------ */

const domainLookup: Record<string, string | null> = domainMap;

const API_BASE_URL =
  (window as any).env?.REACT_APP_API_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:4000/api';

/** Exact match, then case-insensitive match, against the domain map. */
function resolveDomain(name: string): string | null {
  const trimmed = name.trim();
  const exact = domainLookup[trimmed];
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  for (const [key, d] of Object.entries(domainLookup)) {
    if (d && key.toLowerCase() === lower) return d;
  }
  return null;
}

// A law faculty almost always shares its parent university's domain
// ("Columbia University School of Law" → columbia.edu). Resume strings name the
// faculty in many forms the map doesn't key on, so when the full string misses
// we strip the law-faculty suffix and re-resolve the parent. We only trust an
// ACADEMIC domain — the map carries a few stray commercial hits from its
// autocomplete build (e.g. "Cooley Law School" → an unrelated .com), and this
// guard keeps those from leaking onto a school logo.
const LAW_FACULTY_SUFFIX_RE =
  /[,\s]+(pritzker school of law|carey law school|college of the law|school of law|college of law|faculty of law|law school|law center|law centre|law campus)$/i;
const ACADEMIC_CC_DOMAINS = new Set(['utoronto.ca', 'ubc.ca', 'mcgill.ca', 'uottawa.ca', 'yorku.ca']);
function isAcademicDomain(domain: string): boolean {
  const d = domain.toLowerCase();
  return /\.edu(\.|$)/.test(d) || /\.ac\.[a-z]{2,}/.test(d) || ACADEMIC_CC_DOMAINS.has(d);
}

/**
 * Build favicon URL through our backend proxy.
 * `kind: 'university'` enables the law-faculty → parent-university fallback.
 */
function getLogoUrl(name: string, kind: 'company' | 'university' = 'company'): string | null {
  if (!name?.trim()) return null;
  const trimmed = name.trim();

  let domain = resolveDomain(trimmed);

  // University fallback: resolve a law faculty through its parent university.
  if (!domain && kind === 'university' && LAW_FACULTY_SUFFIX_RE.test(trimmed)) {
    const parent = trimmed.replace(LAW_FACULTY_SUFFIX_RE, '').replace(/[,\s]+$/, '');
    const parentDomain = resolveDomain(parent);
    if (parentDomain && isAcademicDomain(parentDomain)) domain = parentDomain;
  }

  if (!domain) {
    // If the name looks like a hostname (contains a dot, no spaces), use it directly
    if (trimmed.includes('.') && !trimmed.includes(' ')) {
      domain = trimmed;
    } else {
      return null;
    }
  }

  return `${API_BASE_URL}/icon?domain=${encodeURIComponent(domain)}&sz=128`;
}

/* ------------------------------------------------------------------ */
/*  Company Logo                                                       */
/* ------------------------------------------------------------------ */

export function CompanyLogo({ company, size = 'sm' }: { company: string; size?: 'sm' | 'xs' | 'md' }) {
  const [imageError, setImageError] = useState(false);
  const sizeClass = size === 'xs' ? 'w-4 h-4' : size === 'md' ? 'w-8 h-8' : 'w-5 h-5';
  const fallbackIconSize = size === 'xs' ? 'w-2.5 h-2.5' : size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
  const logoUrl = getLogoUrl(company);
  const handleError = useCallback(() => setImageError(true), []);

  if (logoUrl && !imageError) {
    return (
      <img
        src={logoUrl}
        alt={`${company} logo`}
        className={`${sizeClass} rounded object-contain bg-white`}
        onError={handleError}
      />
    );
  }
  return (
    <div className={`${sizeClass} rounded bg-slate-200 dark:bg-slate-700 flex items-center justify-center`}>
      <Building2 className={`${fallbackIconSize} text-slate-500 dark:text-slate-400`} />
    </div>
  );
}

/** Larger company logo for timeline with initials fallback */
export function TimelineCompanyLogo({ company }: { company: string }) {
  const [imageError, setImageError] = useState(false);
  const logoUrl = getLogoUrl(company);
  const handleError = useCallback(() => setImageError(true), []);

  if (logoUrl && !imageError) {
    return (
      <img
        src={logoUrl}
        alt={`${company} logo`}
        className="w-8 h-8 rounded-lg object-contain bg-white border border-slate-200 dark:border-slate-700"
        onError={handleError}
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center border border-slate-200 dark:border-slate-700">
      <Building2 className="w-4 h-4 text-slate-500 dark:text-slate-400" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Domain Logo — same /icon proxy, but the domain is already known    */
/*  (e.g. a whitelisted email domain), so no name→domain resolution.   */
/* ------------------------------------------------------------------ */

export function DomainLogo({ domain, size = 'sm' }: { domain: string; size?: 'xs' | 'sm' | 'md' }) {
  const [imageError, setImageError] = useState(false);
  const sizeClass = size === 'xs' ? 'w-4 h-4' : size === 'md' ? 'w-8 h-8' : 'w-5 h-5';
  const fallbackIconSize = size === 'xs' ? 'w-2.5 h-2.5' : size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
  const trimmed = domain?.trim();
  const logoUrl = trimmed ? `${API_BASE_URL}/icon?domain=${encodeURIComponent(trimmed)}&sz=128` : null;
  const handleError = useCallback(() => setImageError(true), []);

  if (logoUrl && !imageError) {
    return (
      <img
        src={logoUrl}
        alt={`${domain} logo`}
        className={`${sizeClass} rounded object-contain bg-white`}
        onError={handleError}
      />
    );
  }
  return (
    <div className={`${sizeClass} rounded bg-slate-200 dark:bg-slate-700 flex items-center justify-center`}>
      <Building2 className={`${fallbackIconSize} text-slate-500 dark:text-slate-400`} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  University Logo                                                    */
/* ------------------------------------------------------------------ */

export function UniversityLogo({ university, size = 'sm' }: { university: string; size?: 'sm' | 'md' | 'xs' }) {
  const [imageError, setImageError] = useState(false);
  const sizeClass = size === 'xs' ? 'w-4 h-4' : size === 'md' ? 'w-8 h-8' : 'w-5 h-5';
  const fallbackIconSize = size === 'xs' ? 'w-2.5 h-2.5' : size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
  const logoUrl = getLogoUrl(university, 'university');
  const handleError = useCallback(() => setImageError(true), []);

  if (logoUrl && !imageError) {
    return (
      <img
        src={logoUrl}
        alt={`${university} logo`}
        className={`${sizeClass} rounded object-contain bg-white`}
        onError={handleError}
      />
    );
  }
  return (
    <div className={`${sizeClass} rounded bg-slate-200 dark:bg-slate-700 flex items-center justify-center`}>
      <GraduationCap className={`${fallbackIconSize} text-slate-500 dark:text-slate-400`} />
    </div>
  );
}

export const LinkedInLogo: React.FC<{ className?: string }> = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 24 24" className={`fill-current ${className}`} aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export const GoogleScholarLogo: React.FC<{ className?: string }> = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M12 24a7 7 0 1 1 0-14 7 7 0 0 1 0 14zm0-24L0 9.5l4.838 3.94A8 8 0 0 1 12 10a8 8 0 0 1 7.162 3.44L24 9.5z" fill="#4285F4" />
  </svg>
);

export const BlueTick: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 22 22" className={className} aria-hidden="true">
    <circle cx="11" cy="11" r="11" fill="#1D9BF0" />
    <path d="M9.64 15.67L5.47 11.5l1.42-1.42 2.75 2.75 5.67-5.66 1.42 1.41-7.09 7.09z" fill="white" />
  </svg>
);
